import { convertTimestampSecondsToMs } from '@shared/helpers/date';
import { normalizeAddress } from '@shared/helpers/web3/address';
import { parseWei } from '@shared/helpers/web3/formatters';

import { AdjacentRound } from '../../model/AdjacentRound/adjacentRound';
import { PredictionParticipants } from '../../model/AdjacentRound/roundParticipation';
import { PredictionRoundState } from '../../model/AdjacentRound/roundState';
import {
  PredictionParticipantsDto,
  PredictionRoundsDto,
  PredictionWinnersDto,
  StatesDto,
} from './types';

const participantsDataMapper = (
  participantsDto: PredictionParticipantsDto,
): PredictionParticipants => {
  return participantsDto.reduce<Record<string, bigint>>((acc, participant) => {
    acc[normalizeAddress(participant.wallet)] = BigInt(String(participant.amount));
    return acc;
  }, {});
};

const winnersDataMapper = (participantsDto: PredictionWinnersDto): PredictionParticipants => {
  return participantsDto.reduce<Record<string, bigint>>((acc, participant) => {
    acc[normalizeAddress(participant.winner)] = BigInt(String(participant.amount));
    return acc;
  }, {});
};

export const dataMapper = (roundsDto: PredictionRoundsDto): AdjacentRound[] => {
  return roundsDto
    .map((roundDto, index) => {
      const lockPriceWei = BigInt(roundDto.lockPrice.toString());
      const closePriceWei = BigInt(roundDto.closePrice.toString());
      const priceChangeWei = closePriceWei - lockPriceWei;

      return {
        id: Number(roundDto.roundId),
        closeTimestamp: convertTimestampSecondsToMs(Number(roundDto.closeTimestamp)),
        startTimestamp: convertTimestampSecondsToMs(Number(roundDto.startTimestamp)),
        state: StatesDto[index],
        upParticipants: participantsDataMapper(roundDto.participantsBull),
        downParticipants: participantsDataMapper(roundDto.participantsBear),
        winParticipants: winnersDataMapper(roundDto.winners),
        lockPrice: parseWei({ wei: lockPriceWei, tokenName: 'USDT' }),
        closePrice: parseWei({ wei: closePriceWei, tokenName: 'USDT' }),
        priceChange: parseWei({
          wei: priceChangeWei,
          tokenName: 'USDT',
          formatOptions: {
            showPlusSign: true,
          },
        }),
        totalAmount: parseWei({
          wei: BigInt(roundDto.totalAmount.toString()),
          tokenName: 'USDT',
          formatOptions: {
            stableToDollar: false,
          },
        }),
        bullAmount: parseWei({
          wei: BigInt(roundDto.bullAmount.toString()),
          tokenName: 'USDT',
          formatOptions: {
            stableToDollar: false,
          },
        }),
        bearAmount: parseWei({
          wei: BigInt(roundDto.bearAmount.toString()),
          tokenName: 'USDT',
          formatOptions: {
            stableToDollar: false,
          },
        }),
        rewardAmount: parseWei({
          wei: BigInt(roundDto.rewardAmount.toString()),
          tokenName: 'USDT',
          formatOptions: {
            stableToDollar: false,
          },
        }),
      };
    })
    .sort((a, b) => {
      const stateOrder = {
        [PredictionRoundState.CLOSED]: 0,
        [PredictionRoundState.LOCKED]: 1,
        [PredictionRoundState.ACTIVE]: 2,
      };

      return stateOrder[a.state] - stateOrder[b.state];
    })
    .filter((roundDto) => !!roundDto.id);
};
